import { ApprovalFeeOutcome } from "@/api/enums";
import type { CancellationPolicyPreview } from "@/api/contracts/appointments.contract";

export type CancellationPolicyTone = "info" | "success" | "warning";

export interface CancellationPolicyNotice {
  tone: CancellationPolicyTone;
  /** The headline consequence of cancelling right now. */
  headline: string;
  /** The rule behind it, for the patient who wants to know why. */
  detail: string;
  /** True when cancelling right now costs the patient nothing. */
  isFree: boolean;
}

/** A cents amount as the dollar string the copy renders. */
export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * What cancelling this appointment right now would cost, in the words the
 * patient sees next to the cancel button.
 *
 * Every number here comes from the API's `cancellationPolicy`: the notice
 * window, the amount refunded, the amount kept. Nothing is recomputed from the
 * start time. The API is what decides where the money goes when the request
 * lands, so a second copy of the rule living here could only ever drift from it
 * and mislead the patient at the moment they commit.
 */
export function getCancellationPolicyNotice(
  policy: CancellationPolicyPreview,
): CancellationPolicyNotice {
  const { noticeHours, retainedAmountCents, refundAmountCents } = policy;

  if (retainedAmountCents > 0) {
    const fee = formatCents(retainedAmountCents);
    return {
      tone: "warning",
      headline: `Cancelling now means the ${fee} appointment fee is not refunded.`,
      detail: `Your appointment is less than ${noticeHours} hours away, so the doctor's time can no longer be offered to another patient. The ${fee} you paid when this appointment was confirmed is kept as the late cancellation fee. Nothing further is charged.`,
      isFree: false,
    };
  }

  if (refundAmountCents > 0) {
    const refund = formatCents(refundAmountCents);
    return {
      tone: "success",
      headline: `Cancelling now is free, and the ${refund} appointment fee is refunded in full.`,
      detail: `You are cancelling at least ${noticeHours} hours ahead, so no cancellation fee applies. The refund goes back to the card you paid with, normally within 5 to 10 business days.`,
      isFree: true,
    };
  }

  // Nothing settled, so there is nothing to keep and nothing to give back. The
  // usual case is a request the doctor has not confirmed yet.
  return {
    tone: "info",
    headline: "Cancelling now is free.",
    detail:
      "Your card has not been charged for this appointment, so there is nothing to refund and nothing to pay.",
    isFree: true,
  };
}

export interface RefundOutcomeNotice {
  tone: CancellationPolicyTone;
  message: string;
}

/**
 * What happened to the approval fee on an appointment that is already over, in
 * the patient's terms.
 *
 * `RefundOwed` deliberately reads as a refund in progress. The patient is owed
 * the money and will get it, an admin is putting it through by hand, and the
 * cancellation email has already promised it, so telling them a refund
 * "failed" would both contradict that email and worry them about money that is
 * already on its way.
 */
export function getRefundOutcomeNotice(appointment: {
  approvalFeeOutcome: ApprovalFeeOutcome;
  approvalRefundedCents: number;
  approvalRefundedOnUtc: string | null;
}): RefundOutcomeNotice | null {
  const { approvalFeeOutcome, approvalRefundedCents, approvalRefundedOnUtc } =
    appointment;

  switch (approvalFeeOutcome) {
    case ApprovalFeeOutcome.Refunded:
    case ApprovalFeeOutcome.RefundedExternally:
      return {
        tone: "success",
        message: approvalRefundedOnUtc
          ? `${formatCents(approvalRefundedCents)} was refunded to the card you paid with on ${new Date(approvalRefundedOnUtc).toLocaleDateString()}. Refunds normally reach your account within 5 to 10 business days.`
          : `${formatCents(approvalRefundedCents)} is being refunded to the card you paid with.`,
      };

    case ApprovalFeeOutcome.RefundOwed:
      return {
        tone: "success",
        message:
          "Your refund is being processed and will go back to the card you paid with. It normally arrives within 5 to 10 business days.",
      };

    case ApprovalFeeOutcome.RetainedLateCancellation:
      return {
        tone: "warning",
        message:
          "The appointment fee you paid when this appointment was confirmed was kept as the late cancellation fee. Nothing further was charged.",
      };

    case ApprovalFeeOutcome.RetainedNoShow:
      return {
        tone: "warning",
        message:
          "The appointment fee you paid when this appointment was confirmed was kept as the no-show fee. Nothing further was charged.",
      };

    default:
      return null;
  }
}
